import React from 'react';
import { fmtChangeFixed } from '../../utils/formatters';
import { formatRange } from '../../utils/dataEnds';
import { GROUP_ORDER, GROUP_SHORT, NotYet, Pattern, rangeText } from '../../utils/weather';
import DeviationBar from './DeviationBar';
import { NOTE } from './weatherUi';

// By category: a small bar with its range for each group whose pattern is
// shown, on one scale across the card; a quiet line for each group still at a
// gate, folded into one line ("Not yet: whole day, food."); a failed holdout's
// window rides in that line's title. The card's lead group is its big figure,
// so it is not repeated here.

export default function GroupBars({ condition, patterns, notYet, lead = 'store' }: {
  condition: string; patterns: Pattern[]; notYet: NotYet[]; lead?: string;
}) {
  const rows = GROUP_ORDER.filter((g) => g !== lead).map((group) => ({
    group,
    shown: patterns.find((p) => p.group === group && p.condition === condition && p.status === 'shown'),
    gated: notYet.find((n) => (n.group ?? 'store') === group && n.condition === condition),
  })).filter((r) => r.shown || r.gated);
  if (!rows.length) return null;
  const shownRows = rows.filter((r) => r.shown);
  const gatedRows = rows.filter((r) => !r.shown && r.gated);
  const scale = Math.max(0.05, ...shownRows.flatMap((r) => [Math.abs(r.shown!.low), Math.abs(r.shown!.high), Math.abs(r.shown!.effect)]));
  const tested = gatedRows
    .filter((r) => r.gated!.status === 'failed_holdout' && r.gated!.holdout)
    .map((r) => `${GROUP_SHORT[r.group]}: tested on ${formatRange(r.gated!.holdout!.window_start, r.gated!.holdout!.window_end)}, and it did not hold up.`);
  return (
    <div>
      <h4 className="font-sans text-xs font-medium text-[#6B6670]">By category</h4>
      {shownRows.length > 0 && (
        <ul className="mt-2 space-y-2">
          {shownRows.map(({ group, shown }) => (
            <li key={group} data-group={group} className="grid grid-cols-[6.5rem_minmax(0,1fr)_3.5rem] items-center gap-2">
              <span className="truncate font-sans text-xs text-[#2D2A2E]">{GROUP_SHORT[group]}</span>
              <DeviationBar point={shown!.effect} low={shown!.low} high={shown!.high} scale={scale} />
              <span className="text-right font-sans text-xs font-medium tabular-nums text-[#2D2A2E]">
                {fmtChangeFixed(shown!.effect * 100)}
                <span className="sr-only"> ({rangeText(Math.min(shown!.low, shown!.high), Math.max(shown!.low, shown!.high))})</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {gatedRows.length > 0 && (
        <p data-gated title={tested.length ? tested.join(' ') : undefined} className={`${NOTE} mt-2`}>
          Not yet: {gatedRows.map((r) => (GROUP_SHORT[r.group] ?? r.group).toLowerCase()).join(', ')}.
        </p>
      )}
    </div>
  );
}
